/**
 * Schematic frames: the scene at chosen play times as flat SVG. Boxes come from `measureScene` (solved layout, measured text), not
 * from lighting or materials, so a frame answers "where is everything and does it collide", never "how does it look".
 */
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { SceneObject } from '../../core/SceneObject';
import type { TraceScene } from '../../core/TraceScene';
import { Renderer } from '../../behaviors/renderer';
import type { Box } from '../quality/geometry';
import { checkMeasured } from '../quality/checks';
import { measureScene } from '../quality/measure';
import type { MeasuredNode, MeasuredScene, QualityRule } from '../quality/types';
import { makeBudget, withRun } from './runtime';
import type { Run } from './runtime';

export interface SchematicOptions {
    /** Play times to draw, seconds. Default `[0, 1.5]`. */
    times?: number[];
    /** Pixels. Default 960 x 600. */
    viewport?: { width: number; height: number };
    /** Where to look from. Default: the scene's own camera, else a camera that frames everything. */
    camera?: { position: [number, number, number]; target: [number, number, number]; fovY?: number };
    /** Red outlines with numbers. Default: the spatial rule violations the quality checks find in each frame. */
    highlight?: { nodePath: string; label?: string }[];
    /** Most nodes drawn, largest on screen first. Default 400. */
    maxNodes?: number;
    /** Wall-clock cap for the call, milliseconds. Default 3000. When it runs out the frames drawn so far come back with `truncated: true`. */
    timeBudgetMs?: number;
}

export type SchematicFrames = { time: number; svg: string }[] & { truncated?: true };

const DEFAULT_VIEWPORT = { width: 960, height: 600 };
const DEFAULT_TIMES = [0, 1.5];
const DEFAULT_MAX_NODES = 400;
const HIGHLIGHT_RULES: ReadonlySet<QualityRule> = new Set<QualityRule>(['overlap', 'clipping', 'text-overflow', 'glyph', 'contrast']);
const MAX_HIGHLIGHTS = 12;
const RED = '#e5231b';
const KIND_RANK: Record<MeasuredNode['kind'], number> = { solid: 0, panel: 1, image: 2, container: 3, group: 4, text: 5 };

type P2 = readonly [number, number];
interface View { view: Matrix; viewProj: Matrix; right: Vector3; width: number; height: number; rh: boolean }

const esc = (s: string): string => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
const n1 = (v: number): string => (Math.round(v * 10) / 10).toString();

function corners(box: Box): Vector3[] {
    const out: Vector3[] = [];
    for (const a of [-1, 1]) for (const b of [-1, 1]) for (const c of [-1, 1]) {
        out.push(new Vector3(
            box.center[0] + a * box.half[0] * box.axes[0][0] + b * box.half[1] * box.axes[1][0] + c * box.half[2] * box.axes[2][0],
            box.center[1] + a * box.half[0] * box.axes[0][1] + b * box.half[1] * box.axes[1][1] + c * box.half[2] * box.axes[2][1],
            box.center[2] + a * box.half[0] * box.axes[0][2] + b * box.half[1] * box.axes[1][2] + c * box.half[2] * box.axes[2][2],
        ));
    }
    return out;
}

function sceneBounds(measured: MeasuredScene): { center: Vector3; radius: number } {
    const min = new Vector3(Infinity, Infinity, Infinity), max = new Vector3(-Infinity, -Infinity, -Infinity);
    for (const node of measured.nodes) if (node.box) for (const c of corners(node.box)) { min.minimizeInPlace(c); max.maximizeInPlace(c); }
    if (!Number.isFinite(min.x)) return { center: Vector3.Zero(), radius: 1 };
    return { center: min.add(max).scale(0.5), radius: Math.max(0.1, max.subtract(min).length() / 2) };
}

function viewOf(run: Run, measured: MeasuredScene, opts: SchematicOptions, width: number, height: number): View {
    const babylon = run.trace.getBabylonScene();
    const rh = babylon.useRightHandedSystem;
    const aspect = width / height;
    let view: Matrix, proj: Matrix;
    const active = babylon.activeCamera;
    if (opts.camera || !active) {
        let eye: Vector3, target: Vector3, fovY = opts.camera?.fovY ?? 0.8;
        if (opts.camera) { eye = Vector3.FromArray(opts.camera.position); target = Vector3.FromArray(opts.camera.target); }
        else {
            const { center, radius } = sceneBounds(measured);
            target = center;
            eye = center.add(new Vector3(0, 0, (rh ? 1 : -1) * radius / Math.sin(fovY / 2) * 1.1));
        }
        view = rh ? Matrix.LookAtRH(eye, target, Vector3.Up()) : Matrix.LookAtLH(eye, target, Vector3.Up());
        proj = rh ? Matrix.PerspectiveFovRH(fovY, aspect, 0.01, 1000) : Matrix.PerspectiveFovLH(fovY, aspect, 0.01, 1000);
    } else {
        view = active.getViewMatrix(true);
        if (active.mode === 1) proj = active.getProjectionMatrix(true);
        else {
            // The camera's fov in its own mode, redrawn at this viewport's aspect.
            const fovY = active.fovMode === 0 ? active.fov : 2 * Math.atan(Math.tan(active.fov / 2) / aspect);
            proj = rh ? Matrix.PerspectiveFovRH(fovY, aspect, Math.max(0.01, active.minZ || 0.01), 1000) : Matrix.PerspectiveFovLH(fovY, aspect, Math.max(0.01, active.minZ || 0.01), 1000);
        }
    }
    const inv = Matrix.Invert(view);
    return { view, viewProj: view.multiply(proj), right: Vector3.TransformNormal(Vector3.Right(), inv).normalize(), width, height, rh };
}

function project(v: View, p: Vector3): { x: number; y: number; depth: number } | null {
    const cam = Vector3.TransformCoordinates(p, v.view);
    const depth = v.rh ? -cam.z : cam.z;
    if (depth <= 0.01) return null;
    const ndc = Vector3.TransformCoordinates(p, v.viewProj);
    return { x: (ndc.x + 1) / 2 * v.width, y: (1 - ndc.y) / 2 * v.height, depth };
}

function hull(points: P2[]): P2[] {
    const pts = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    if (pts.length < 3) return pts;
    const cross = (o: P2, a: P2, b: P2) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const half = (list: P2[]) => {
        const out: P2[] = [];
        for (const p of list) { while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], p) <= 0) out.pop(); out.push(p); }
        out.pop();
        return out;
    };
    return [...half(pts), ...half([...pts].reverse())];
}

interface Shape { node: MeasuredNode; poly: P2[]; bbox: [number, number, number, number]; area: number; depth: number; pxPerM: number; center: P2; angle: number }

function shapeOf(v: View, node: MeasuredNode): Shape | null {
    if (!node.box) return null;
    const projected = corners(node.box).map(c => project(v, c));
    if (projected.some(p => !p)) return null;
    const pts = projected.map(p => [p!.x, p!.y] as P2);
    const poly = hull(pts);
    const xs = pts.map(p => p[0]), ys = pts.map(p => p[1]);
    const bbox: Shape['bbox'] = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
    if (bbox[2] < 0 || bbox[0] > v.width || bbox[3] < 0 || bbox[1] > v.height) return null;
    const center = new Vector3(...node.box.center);
    const c = project(v, center)!;
    const px = project(v, center.add(v.right))!;
    const pxPerM = Math.hypot(px.x - c.x, px.y - c.y);
    const axis = new Vector3(...node.box.axes[0]);
    const a = project(v, center.add(axis))!;
    return { node, poly, bbox, area: (bbox[2] - bbox[0]) * (bbox[3] - bbox[1]), depth: c.depth, pxPerM, center: [c.x, c.y], angle: Math.atan2(a.y - c.y, a.x - c.x) * 180 / Math.PI };
}

function lineBreak(content: string, lineCount: number): string[] {
    if (lineCount <= 1) return content.split(/\r?\n/);
    const budget = Math.ceil(content.length / lineCount * 1.1);
    const lines: string[] = [];
    let line = '';
    for (const word of content.split(/\s+/).filter(Boolean)) {
        if (line && (line + ' ' + word).length > budget && lines.length < lineCount - 1) { lines.push(line); line = word; }
        else line = line ? line + ' ' + word : word;
    }
    if (line) lines.push(line);
    return lines;
}

function solidFill(object: SceneObject | undefined): string {
    const color = object?.getBehavior(Renderer)?._getResolvedData().materials?.[0]?.baseColorFactor;
    return typeof color === 'string' && /^#[0-9a-fA-F]{6}/.test(color) ? color.slice(0, 7) : '#9aa1ad';
}

const points = (poly: readonly P2[]): string => poly.map(p => `${n1(p[0])},${n1(p[1])}`).join(' ');

function drawFrame(run: Run, measured: MeasuredScene, time: number, opts: SchematicOptions): string {
    const { width, height } = opts.viewport ?? DEFAULT_VIEWPORT;
    const v = viewOf(run, measured, opts, width, height);
    const byPath = new Map(run.trace.getSceneObjects().map(o => [o.getPath(), o]));
    const nodes = measured.nodes.filter(n => !run.scope || run.scope.has(n.path));
    let shapes = nodes.map(n => shapeOf(v, n)).filter((s): s is Shape => !!s);

    const wanted = opts.highlight ?? checkMeasured(measured).violations
        .filter(x => HIGHLIGHT_RULES.has(x.rule)).map(x => ({ nodePath: x.nodePath, label: x.rule }));
    const marks: { n: number; label: string; shape: Shape }[] = [];
    for (const h of wanted) {
        if (marks.length >= MAX_HIGHLIGHTS) break;
        const shape = shapes.find(s => s.node.path === h.nodePath);
        if (shape && !marks.some(m => m.shape === shape)) marks.push({ n: marks.length + 1, label: h.label ?? '', shape });
    }

    const max = opts.maxNodes ?? DEFAULT_MAX_NODES;
    if (shapes.length > max) {
        const keep = new Set(marks.map(m => m.shape));
        const rest = shapes.filter(s => !keep.has(s)).sort((a, b) => b.area - a.area).slice(0, Math.max(0, max - keep.size));
        shapes = [...keep, ...rest];
    }
    shapes.sort((a, b) => Math.abs(a.depth - b.depth) < 1e-4 ? KIND_RANK[a.node.kind] - KIND_RANK[b.node.kind] : b.depth - a.depth);

    const out: string[] = [];
    out.push(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${width} ${height}" width="${width}" height="${height}" font-family="Roboto, Helvetica, Arial, sans-serif">`);
    out.push(`<rect width="${width}" height="${height}" fill="#eef0f3"/>`);
    for (const s of shapes) {
        const { node } = s;
        if (node.kind === 'text' && node.text) {
            const t = node.text, size = Math.max(1, t.emM * s.pxPerM);
            const lines = lineBreak(t.content, t.lineCount);
            const lead = size * 1.2, top = s.center[1] - (lines.length - 1) * lead / 2;
            const fill = t.color?.hex ?? '#111111';
            const spans = lines.map((l, i) => `<tspan x="${n1(s.center[0])}" y="${n1(top + i * lead)}">${esc(l)}</tspan>`).join('');
            out.push(`<text font-size="${n1(size)}" fill="${fill}" fill-opacity="${t.color?.alpha ?? 1}" text-anchor="middle" dominant-baseline="central" transform="rotate(${n1(s.angle)} ${n1(s.center[0])} ${n1(s.center[1])})" data-node="${esc(node.path)}">${spans}</text>`);
        } else if (node.kind === 'panel') {
            out.push(`<polygon points="${points(s.poly)}" fill="${node.fill?.hex ?? '#ffffff'}" fill-opacity="${node.fill?.alpha ?? 1}" stroke="#c9ced6" stroke-width="0.5" data-node="${esc(node.path)}"/>`);
        } else if (node.kind === 'solid') {
            out.push(`<polygon points="${points(s.poly)}" fill="${solidFill(byPath.get(node.path))}" fill-opacity="0.85" stroke="#6b7280" stroke-width="0.75" data-node="${esc(node.path)}"/>`);
        } else if (node.kind === 'image') {
            out.push(`<polygon points="${points(s.poly)}" fill="#d9dde3" stroke="#9aa1ad" stroke-width="0.75" data-node="${esc(node.path)}"/>`);
            out.push(`<text x="${n1(s.center[0])}" y="${n1(s.center[1])}" font-size="11" fill="#6b7280" text-anchor="middle" dominant-baseline="central">image</text>`);
        } else if (node.kind === 'container') {
            out.push(`<polygon points="${points(s.poly)}" fill="none" stroke="#9aa1ad" stroke-opacity="0.45" stroke-width="0.75" stroke-dasharray="4 3" data-node="${esc(node.path)}"/>`);
        }
    }
    for (const m of marks) {
        const [x0, y0, x1, y1] = m.shape.bbox;
        out.push(`<rect x="${n1(x0 - 2)}" y="${n1(y0 - 2)}" width="${n1(x1 - x0 + 4)}" height="${n1(y1 - y0 + 4)}" fill="none" stroke="${RED}" stroke-width="2"/>`);
        out.push(`<circle cx="${n1(x0 - 2)}" cy="${n1(y0 - 2)}" r="9" fill="${RED}"/><text x="${n1(x0 - 2)}" y="${n1(y0 - 2)}" font-size="11" font-weight="700" fill="#fff" text-anchor="middle" dominant-baseline="central">${m.n}</text>`);
    }
    out.push(`<text x="12" y="20" font-size="14" font-weight="700" fill="#1f2937">t = ${n1(time)} s</text>`);
    marks.forEach((m, i) => out.push(`<text x="12" y="${height - 10 - (marks.length - 1 - i) * 15}" font-size="11" fill="${RED}">${m.n}  ${esc(m.label)}  ${esc(m.shape.node.path)}</text>`));
    out.push('</svg>');
    return out.join('\n');
}

/** The scene at each of `opts.times` (seconds of play from the start), as an SVG per time. The source scene is not touched. */
export async function schematicFrames(trace: TraceScene, objects?: readonly SceneObject[], opts: SchematicOptions = {}): Promise<SchematicFrames> {
    const times = [...new Set(opts.times?.length ? opts.times : DEFAULT_TIMES)].sort((a, b) => a - b);
    const budget = makeBudget(opts.timeBudgetMs, 3000);
    const frames: SchematicFrames = [];
    const done = await withRun(trace, objects, budget, async run => {
        for (const time of times) {
            await run.advanceTo(time * 1000);
            if (run.truncated) return false;
            frames.push({ time, svg: drawFrame(run, measureScene(run.trace), time, opts) });
        }
        return true;
    });
    if (!done) frames.truncated = true;
    return frames;
}
